import * as React from 'react';

import { BoxProps } from '@leagueplatform/genesis-commons';
import { Box, queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import {
  APP_NAV_DEFAULT_HEIGHT,
  APP_NAV_MOBILE_HEIGHT,
  AuthedWebviewModal,
  DraggableLiveChat,
  FEATURE_FLAGS,
  INTERNAL_BASE_ROUTES,
  LinkHandler,
  LoadingSpinner,
  MODAL_PARAMS,
  QueryParamModal,
  useSsoDeepLink,
  ImpersonationMasonryBanner,
  ErrorBoundary,
} from '@highmark-web/highmark-common';
import { useLocation } from 'react-router-dom';
import { useGetAppFeatureFlag } from 'hooks/use-get-app-feature-flag.hook';
import { AppNavBarComponent } from './nav.component';
import { BCBSWyFooter } from './footer/bcbswy-footer.component';

const ExcludeChatBubbleRoutes = [
  INTERNAL_BASE_ROUTES.COORDINATION_OF_BENEFITS_BASE,
];

export const PrivateBasePage: React.FC<BoxProps> = ({
  enableLiveChat,
  enableEbillRegister,
  children,
}) => {
  useScrollTopOnLocationChange();
  useSsoDeepLink(LinkHandler);

  const { isEnabled: isLiveChatNavigateAwayEnabled } = useGetAppFeatureFlag(
    FEATURE_FLAGS.LIVE_CHAT_NAVIGATE_AWAY,
  );

  const location = useLocation();
  // Get first path string before non character value (/, ?, etc)
  const basePath = location?.pathname?.match(/^\/[\w-]+/) ?? ['/'];
  const isChatExcludedPath = ExcludeChatBubbleRoutes.includes(basePath[0]);

  const isChatBubbleEnabled = Boolean(
    !isChatExcludedPath && enableLiveChat && isLiveChatNavigateAwayEnabled,
  );

  const isMobile = useMediaQuery(queryHelpers.down('mobileLandscape'));

  const MAIN_CONTENT_MIN_HEIGHT = `calc(100vh - ${
    isMobile ? APP_NAV_MOBILE_HEIGHT : APP_NAV_DEFAULT_HEIGHT
  }px)`;

  const customModalParams = [];

  if (enableEbillRegister) {
    customModalParams.push(MODAL_PARAMS.EBILL_REGISTRATION);
  }

  return (
    <>
      <AppNavBarComponent />
      <AuthedWebviewModal />
      <QueryParamModal additionalCustomModalParams={customModalParams} />
      <ImpersonationMasonryBanner />
      <Box
        as="main"
        id="main-content"
        css={{
          position: 'relative',
          flex: '1',
          overflowX: 'hidden',
          // This is so any page will take up the whole screen regardless of content
          minHeight: MAIN_CONTENT_MIN_HEIGHT,
          paddingBottom: '$twoAndHalf',
        }}
      >
        <ErrorBoundary
          styleErrorProps={{
            minHeight: MAIN_CONTENT_MIN_HEIGHT,
          }}
          contextLocation="BasePage ErrorBoundary"
        >
          <React.Suspense fallback={<LoadingSpinner />}>
            {children}
          </React.Suspense>
        </ErrorBoundary>
      </Box>
      <BCBSWyFooter />
      {isChatBubbleEnabled && <DraggableLiveChat />}
    </>
  );
};
